import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { INavLink, Nav } from '@fluentui/react';
import { IWithErrorHandlingProps } from './withErrorHandling';
import { OrbUserContext } from './OrbUserContext';
import { ThemeProvider } from '@fluentui/react/lib/Theme';
import { darkBlueTheme } from '../AppGlobals';
import { _onRenderNavLink } from './ZedXMainMenu';
import { LoggedUserInfoForMenu } from './LoggedUserInfoForMenu';
import { TruncatedText } from './TruncatedText';
import { IEntityDescription1 } from '../types';

interface ITrackRisksMenu extends IWithErrorHandlingProps {
    onLinkClicked?: () => void;
    entityDescriptionForMenu: IEntityDescription1;
}

type ContextualInfo = {
    isContextualMenu: boolean;
    selectedLabel?: string;
};

export const TrackRisksMenu = ({
    entityDescriptionForMenu,
    errorHandling: { clearErrors },
    onLinkClicked
}: ITrackRisksMenu): React.ReactElement => {

    const imagesRoot: string = process.env.REACT_APP_ZedXImagesRoot!;
    const { userPermissions: up } = useContext(OrbUserContext);
    const history = useHistory();
    const { pathname } = useLocation();

    const [navs, setNavs] = useState<INavLink[]>([]);
    const resetErrors = useCallback(clearErrors, [clearErrors]);
    const [totalUpdates] = useState(0);

    const [manageRisksExpanded, setManageRisksExpanded] = useState(false);
    const [manageIssuesExpanded, setManageIssuesExpanded] = useState(false);
    const [manageIncidentsExpanded, setManageIncidentsExpanded] = useState(false);
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const hasInitialisedFromRoute = useRef(false);

    /* -------------------------------------------------------
       Parse contextual menu mode
    -------------------------------------------------------- */
    const parseContextualMenu = (path: string): ContextualInfo => {

        if (/^\/a3\/risk-mitigating-actions-for-risk\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Risk mitigating actions' };
        }

        if (/^\/a3\/sign-off-for-risk\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Risk Updates' };
        }

        if (/^\/a3\/issues-actions-for-issue\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Corrective Actions' };
        }

        if (/^\/a3\/incidents-actions-for-incident\/[^/]+/.test(path)) {
            return { isContextualMenu: true, selectedLabel: 'Corrective Actions' };
        }

        return { isContextualMenu: false };
    };

    const { isContextualMenu, selectedLabel } = parseContextualMenu(pathname);

    /* -------------------------------------------------------
       One-time auto-expand based on initial route (GLOBAL MENU)
    -------------------------------------------------------- */
    useEffect(() => {
        if (hasInitialisedFromRoute.current || isContextualMenu) return;

        if (
            pathname.startsWith('/a3/risks') ||
            pathname.startsWith('/a3/risk-mitigating-actions')
        ) {
            setManageRisksExpanded(true);
        }

        if (
            pathname.startsWith('/a3/issues') ||
            pathname.startsWith('/a3/issues-actions')
        ) {
            setManageIssuesExpanded(true);
        }

        if (
            pathname.startsWith('/a3/incidents') ||
            pathname.startsWith('/a3/incidents-actions')
        ) {
            setManageIncidentsExpanded(true);
        }

        if (
            pathname.startsWith('/a3/settings/customFields') ||
            pathname.startsWith('/a3/settings/risk-types') ||
            pathname.startsWith('/a3/settings/heatmap-config')
        ) {
            setSettingsExpanded(true);
        }

        hasInitialisedFromRoute.current = true;
    }, [pathname, isContextualMenu]);

    /* -------------------------------------------------------
       Build navigation
    -------------------------------------------------------- */
    useEffect(() => {
        const routerNav = (path: string): void => {
            resetErrors();
            history.push(path);
            if (onLinkClicked) onLinkClicked();
        };

        const NamePathToNavLink = ({ name, path }): INavLink => ({
            key: path,
            name,
            url: `#${path}`,
            onClick: () => routerNav(path)
        });

        /* -------- CONTEXTUAL MENU -------- */
        if (isContextualMenu && selectedLabel) {
            const links: INavLink[] = [
                { name: 'Home', path: '/' },
                { name: 'Welcome', path: '/a3/welcome' },
                { name: selectedLabel, path: pathname }
            ].map(NamePathToNavLink);

            setNavs(links);
            return;
        }

        /* -------- GLOBAL MENU -------- */
        const links: INavLink[] = [
            { name: 'Home', path: '/' },
            { name: 'Welcome', path: '/a3/welcome' },
            { name: 'Insights', path: '/a3/insights' }
        ].map(NamePathToNavLink);

        if (up.UserCanViewRiskEntities()) {

            links.push({
                key: 'manage_risks',
                name: 'Manage Risks',
                url: null,
                isExpanded: manageRisksExpanded,
                onClick: () => setManageRisksExpanded(prev => !prev),
                links: [
                    { name: 'Risks', path: '/a3/risks' },
                    { name: 'Risk mitigating actions', path: '/a3/risk-mitigating-actions' }
                ].map(NamePathToNavLink)
            });

            links.push({
                key: 'manage_issues',
                name: 'Manage Issues',
                url: null,
                isExpanded: manageIssuesExpanded,
                onClick: () => setManageIssuesExpanded(prev => !prev),
                links: [
                    { name: 'Issues', path: '/a3/issues' },
                    { name: 'Corrective Actions', path: '/a3/issues-actions' }
                ].map(NamePathToNavLink)
            });

            links.push({
                key: 'manage_incidents',
                name: 'Manage Incidents',
                url: null,
                isExpanded: manageIncidentsExpanded,
                onClick: () => setManageIncidentsExpanded(prev => !prev),
                links: [
                    { name: 'Incidents', path: '/a3/incidents' },
                    { name: 'Corrective Actions', path: '/a3/incidents-actions' }
                ].map(NamePathToNavLink)
            });

            if (up.UserIsSystemAdmin()) {
                links.push({
                    key: 'risk_settings',
                    name: 'Settings',
                    url: null,
                    isExpanded: settingsExpanded,
                    onClick: () => setSettingsExpanded(prev => !prev),
                    links: [
                        { name: 'Custom Fields', path: '/a3/settings/customFields' },
                        { name: 'Risk types', path: '/a3/settings/risk-types' },
                        { name: 'Heatmap Config', path: '/a3/settings/heatmap-config' }
                    ].map(NamePathToNavLink)
                });
            }
        }

        links.push(NamePathToNavLink({ name: 'Outputs', path: '/a3/outputs' }));

        setNavs(links);

    }, [
        up,
        history,
        onLinkClicked,
        resetErrors,
        pathname,
        isContextualMenu,
        selectedLabel,
        manageRisksExpanded,
        manageIssuesExpanded,
        manageIncidentsExpanded,
        settingsExpanded
    ]);

    /* -------------------------------------------------------
       Render
    -------------------------------------------------------- */
    return (
        <ThemeProvider theme={darkBlueTheme}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>

                {/* Logo */}
                <div
                    style={{
                        width: '100%',
                        paddingTop: 0,
                        paddingBottom: '1px',
                        paddingLeft: '15px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    <img
                        src={`${imagesRoot}/leftnav/LeftMenu-TrackRisks.png`}
                        alt="Track Risks"
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </div>

                {/* Logged-in user */}
                <LoggedUserInfoForMenu />

                {/* Contextual header */}
                {isContextualMenu && entityDescriptionForMenu && (
                    <div
                        style={{
                            backgroundColor: '#1A619F',
                            color: 'white',
                            padding: '10px 15px',
                            margin: '5px 10px 10px',
                            borderRadius: '4px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                        }}
                    >
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine1} />
                        <TruncatedText text={entityDescriptionForMenu.DescriptionLine2} />
                    </div>
                )}

                {/* Navigation */}
                <Nav
                    selectedKey={pathname}
                    groups={[{ links: navs }]}
                    onRenderLink={(lnk) =>
                        _onRenderNavLink(lnk, totalUpdates)
                    }
                />
            </div>
        </ThemeProvider>
    );
};
